import {
    HEADER_MENUS,
    HEADER,
    MOBILE_HEADER,
} from '@Web/Pages/Web/Layout/Components/Header/constants.js';
import Menu from '@Web/Pages/Web/Layout/Components/Header/Components/Menu';
import { useOnClickOutside } from '@Shared/Hook/UseOnClickOutside.js';
import { router, usePage } from '@inertiajs/react';
import SvgIcon from '@Web/Src/Components/SvgIcon';
import Button from '@Web/Src/Components/Button';
import { useRef, useState, useEffect } from 'react';

function Header({ menuOpened, setMenuOpened, lightTheme }) {
    const isAuth = usePage().props.auth.customer;
    const demo_campaign_url = usePage().props.auth?.demo_campaign_url;
    const menuRef = useRef(null);
    const softwareRef = useRef(null);
    const integrationsRef = useRef(null);
    const resourcesRef = useRef(null);

    const [subMenuOpened, setSubMenuOpened] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);

    useOnClickOutside([menuRef, softwareRef, integrationsRef, resourcesRef], () => {
        setSubMenuOpened(false);
        setActiveMenu(null);
    });

    useEffect(() => {
        if (menuOpened) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpened]);

    const renderRef = (value) => {
        switch (value) {
            case 'software':
                return softwareRef;
            case 'integrations':
                return integrationsRef;
            case 'resources':
                return resourcesRef;
            default:
                break;
        }
    };

    const handleSelectMenu = (menu) => {
        if (subMenuOpened) {
            if (menu !== activeMenu) {
                setActiveMenu(menu);
            } else {
                setSubMenuOpened(!subMenuOpened);
                setActiveMenu(null);
            }
        } else {
            setSubMenuOpened(!subMenuOpened);
            setActiveMenu(menu);
        }
    };

    const handleRegister = () => {
        if (isAuth) {
            router.visit(route('customer.auth.logout'), {
                method: 'POST',
            });
        } else {
            router.visit(route('customer.auth.register'));
        }
    };

    const handleOpenExternalLink = (externalLink) => {
        () => window.open(externalLink, '_blank', 'noopener, noreferrer');
    };

    return (
        <>
            <header
                className={`${lightTheme ? 'relative' : 'absolute'} medium:hidden z-20 block h-28 w-full bg-transparent py-6`}
            >
                <nav className={'mx-auto flex max-w-7xl items-center justify-between px-6'}>
                    <div className={'mr-1 min-w-48 cursor-pointer'}>
                        <span onClick={() => router.visit(route('home'))}>
                            <img
                                className='h-10 sm:h-12'
                                src={`/assets/images/svg/web/logos/${lightTheme ? 'logo-black' : 'logo'}.svg`}
                                alt='Referral Factory Software logo'
                            />
                        </span>
                    </div>
                    <div
                        className={
                            'large:!gap-4 flex w-full items-center justify-center gap-8 text-base font-semibold'
                        }
                    >
                        {HEADER.map((item) => {
                            const { title, hasMenu, value, route } = item;
                            return (
                                <div key={item.title}>
                                    {hasMenu ? (
                                        <div
                                            ref={renderRef(value)}
                                            onClick={() => handleSelectMenu(value)}
                                            className={`${activeMenu === value ? 'text-yellow' : ''} hover:text-yellow flex cursor-pointer items-center ${lightTheme ? 'text-dark' : 'text-white'}`}
                                        >
                                            {title}
                                            <span
                                                className={`ml-1 block ${activeMenu === value ? 'rotate-180 transition' : 'rotate-0 transition'}`}
                                            >
                                                <SvgIcon
                                                    onClick={() => {}}
                                                    collectionName={'header-icons-sprite'}
                                                    iconId={'arrow-down'}
                                                    className={'size-6'}
                                                />
                                            </span>
                                        </div>
                                    ) : (
                                        <div
                                            className={`hover:text-yellow cursor-pointer whitespace-nowrap font-semibold ${lightTheme ? 'text-dark' : 'text-white'} transition`}
                                        >
                                            <span onClick={() => router.visit(route)}>
                                                {title}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {subMenuOpened && (
                            <div
                                className={
                                    'absolute left-1/2 top-full z-20 w-max -translate-x-1/2 rounded-3xl bg-white px-8 py-10 shadow-lg'
                                }
                                ref={menuRef}
                            >
                                <Menu
                                    menuOpened={subMenuOpened}
                                    menu={HEADER_MENUS[activeMenu]}
                                    setSubMenuOpened={setSubMenuOpened}
                                />
                            </div>
                        )}
                    </div>
                    <div
                        className={'flex items-center justify-around gap-4 text-lg font-bold'}
                    >
                        <a
                            href={
                                isAuth
                                    ? route('customer.dashboard.index')
                                    : demo_campaign_url
                                      ? demo_campaign_url
                                      : route('customer.auth.login')
                            }
                        >
                            <Button
                                name={
                                    isAuth
                                        ? __('Dashboard')
                                        : demo_campaign_url
                                          ? __('Demo Builder')
                                          : __('Log In')
                                }
                                className={`!bold hover:!border-yellow hover:!text-yellow !min-w-32 whitespace-nowrap !bg-transparent !text-lg transition ${lightTheme ? '!border-dark !text-dark' : '!border-white !text-white'}`}
                            />
                        </a>
                        <Button
                            onClick={handleRegister}
                            className={'!bold !min-w-32 !text-lg'}
                            name={isAuth ? __('Log Out') : __('Start Trial')}
                        />
                    </div>
                </nav>
            </header>
            <header
                className={`${lightTheme ? 'relative' : 'absolute'} medium:flex z-20 hidden w-full items-center justify-between bg-transparent p-4`}
            >
                <div className={'cursor-pointer'}>
                    <a href={route('home')}>
                        <img
                            src={`/assets/images/svg/web/logos/${lightTheme ? 'logo-black' : 'logo'}.svg`}
                            alt='Referral Factory Software logo'
                            className='h-10 sm:h-12'
                        />
                    </a>
                </div>
                <div className='flex gap-5'>
                    <Button
                        onClick={handleRegister}
                        className={'h-10 !min-w-32 items-center !text-sm !font-semibold'}
                        name={isAuth ? __('Log Out') : __('Start Trial')}
                    />
                    <div
                        onClick={() => {
                            setMenuOpened(true);
                        }}
                        className={`${lightTheme ? 'text-dark' : 'text-light'} flex w-10 items-center justify-center`}
                    >
                        <svg
                            id='align-right'
                            width='24'
                            height='24'
                            viewBox='0 0 24 24'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                fillRule='evenodd'
                                clipRule='evenodd'
                                d='M3.59998 5.535C3.59998 5.13736 3.92233 4.815 4.31998 4.815H19.68C20.0776 4.815 20.4 5.13736 20.4 5.535C20.4 5.93265 20.0776 6.255 19.68 6.255H4.31998C3.92233 6.255 3.59998 5.93265 3.59998 5.535ZM3.59998 14.4947C3.59998 14.097 3.92233 13.7747 4.31998 13.7747H19.68C20.0776 13.7747 20.4 14.097 20.4 14.4947C20.4 14.8923 20.0776 15.2147 19.68 15.2147H4.31998C3.92233 15.2147 3.59998 14.8923 3.59998 14.4947Z'
                                fill='currentColor'
                            />
                            <path
                                fillRule='evenodd'
                                clipRule='evenodd'
                                d='M7.01251 10.0125C7.01251 9.61486 7.33486 9.29248 7.73251 9.29248H19.6788C20.0764 9.29248 20.3988 9.61486 20.3988 10.0125C20.3988 10.4101 20.0764 10.7325 19.6788 10.7325H7.73251C7.33486 10.7325 7.01251 10.4101 7.01251 10.0125ZM7.01251 18.9731C7.01251 18.5755 7.33486 18.2531 7.73251 18.2531H19.6788C20.0764 18.2531 20.3988 18.5755 20.3988 18.9731C20.3988 19.3708 20.0764 19.6931 19.6788 19.6931H7.73251C7.33486 19.6931 7.01251 19.3708 7.01251 18.9731Z'
                                fill='currentColor'
                            />
                        </svg>
                    </div>
                </div>
                {menuOpened && (
                    <div className='fixed left-0 top-0 h-full w-full overflow-y-scroll bg-[#272C35]'>
                        <header
                            className={
                                'medium:flex hidden w-full items-center justify-between bg-transparent p-4'
                            }
                        >
                            <div className={'cursor-pointer'}>
                                {subMenuOpened ? (
                                    <img
                                        onClick={() => {
                                            setSubMenuOpened(false);
                                        }}
                                        className='text-light h-10'
                                        src={'/assets/images/svg/web/header-back.svg'}
                                        alt='back'
                                    />
                                ) : (
                                    <a href={route('home')}>
                                        <img
                                            className='h-10 sm:h-12'
                                            src={'/assets/images/svg/web/logos/logo.svg'}
                                            alt='Referral Factory Software logo'
                                        />
                                    </a>
                                )}
                            </div>
                            <div className='flex gap-5'>
                                <Button
                                    onClick={handleRegister}
                                    className={
                                        'h-10 !min-w-32 items-center !text-sm !font-semibold'
                                    }
                                    name={isAuth ? __('Log Out') : __('Start Trial')}
                                />
                                <div className='flex w-10 items-center justify-center'>
                                    <img
                                        onClick={() => {
                                            setMenuOpened(false);
                                        }}
                                        className='text-light h-10 cursor-pointer'
                                        src={'/assets/images/svg/web/header-close.svg'}
                                        alt='Referral Factory Software logo'
                                    />
                                </div>
                            </div>
                        </header>
                        <div className='mt-20 px-4'>
                            {MOBILE_HEADER.map((item) => {
                                const { title, hasMenu, value, route, externalLink } = item;
                                return (
                                    <div
                                        className='mt-4 text-xl font-semibold'
                                        key={item.title}
                                    >
                                        {hasMenu ? (
                                            <div
                                                ref={renderRef(value)}
                                                onClick={() => handleSelectMenu(value)}
                                                className={`hover:text-yellow text-light small:justify-between flex cursor-pointer items-center justify-normal gap-4 leading-5`}
                                            >
                                                {title}
                                                <span className={`ml-1 block`}>
                                                    <SvgIcon
                                                        onClick={() => {}}
                                                        collectionName={'header-icons-sprite'}
                                                        iconId={'arrow-down'}
                                                        className={'size-6 -rotate-90'}
                                                    />
                                                </span>
                                            </div>
                                        ) : (
                                            <div
                                                className={
                                                    'hover:text-yellow cursor-pointer text-white transition'
                                                }
                                            >
                                                {externalLink ? (
                                                    <p
                                                        onClick={() =>
                                                            handleOpenExternalLink(
                                                                externalLink
                                                            )
                                                        }
                                                    >
                                                        {title}
                                                    </p>
                                                ) : (
                                                    <a href={route}>{title}</a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <div
                                className={'mt-10 flex items-center gap-4 text-lg font-bold'}
                            >
                                <a
                                    href={
                                        isAuth
                                            ? route('customer.dashboard.index')
                                            : route('customer.auth.login')
                                    }
                                >
                                    <Button
                                        name={isAuth ? __('Dashboard') : __('Log in')}
                                        className={
                                            '!bold !min-w-32 !border-white !bg-transparent !text-lg !text-white transition'
                                        }
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
}

export default Header;
